import { useEffect, useState, useCallback } from 'react';
import {
  Loader2, Power, PowerOff, BadgeCheck, Star, Trash2, Search, Phone, Mail,
  Users as UsersIcon,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import adminApi from '../lib/adminApi';

export default function AdminProfissionais() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/profissionais${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      setLista(res.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const toggle = async (p, campo) => {
    await adminApi.patch(`/admin/profissionais/${p.id}`, { [campo]: !p[campo] });
    carregar(busca);
  };

  const excluir = async (p) => {
    if (!window.confirm(`Excluir profissional "${p.nome}"?`)) return;
    await adminApi.delete(`/admin/profissionais/${p.id}`);
    carregar(busca);
  };

  return (
    <AdminLayout title="Profissionais">
      <form
        onSubmit={(e) => { e.preventDefault(); carregar(busca); }}
        className="mb-4 relative max-w-md"
      >
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome, email ou cidade..."
          data-testid="prof-busca-input"
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 pl-9 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]"
        />
      </form>

      {loading ? (
        <div className="py-12 text-center text-gray-400 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Carregando...
        </div>
      ) : lista.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
          <UsersIcon size={28} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Nenhum profissional encontrado.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
          {lista.map((p) => (
            <div key={p.id} data-testid={`prof-${p.slug || p.id}`} className="p-4 hover:bg-gray-50 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-[#E1F5EE] flex items-center justify-center text-[#1D9E75] font-bold text-sm flex-shrink-0">
                  {p.nome?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className={`text-sm font-semibold truncate ${p.ativo ? 'text-gray-900' : 'text-gray-400'}`}>{p.nome}</p>
                    {p.verificado && <BadgeCheck size={13} className="text-[#1D9E75] flex-shrink-0" />}
                    {p.premium && <Star size={13} className="text-amber-500 fill-amber-500 flex-shrink-0" />}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                    <span className="text-xs text-gray-500">{p.cidade}/{p.estado}</span>
                    {p.email && <span className="text-xs text-gray-400 flex items-center gap-1"><Mail size={11} />{p.email}</span>}
                    {p.telefone && <span className="text-xs text-gray-400 flex items-center gap-1"><Phone size={11} />{p.telefone}</span>}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-wrap">
                <button onClick={() => toggle(p, 'ativo')} title={p.ativo ? 'Desativar' : 'Ativar'} data-testid={`toggle-ativo-${p.id}`} className={`p-1.5 rounded-lg ${p.ativo ? 'text-[#1D9E75] hover:bg-[#E1F5EE]' : 'text-gray-400 hover:bg-gray-100'}`}>
                  {p.ativo ? <Power size={14} /> : <PowerOff size={14} />}
                </button>
                <button onClick={() => toggle(p, 'verificado')} title="Verificado" data-testid={`toggle-verif-${p.id}`} className={`p-1.5 rounded-lg ${p.verificado ? 'text-[#1D9E75] hover:bg-[#E1F5EE]' : 'text-gray-400 hover:bg-gray-100'}`}>
                  <BadgeCheck size={14} />
                </button>
                <button onClick={() => toggle(p, 'premium')} title="Premium" data-testid={`toggle-premium-${p.id}`} className={`p-1.5 rounded-lg ${p.premium ? 'text-amber-500 hover:bg-amber-50' : 'text-gray-400 hover:bg-gray-100'}`}>
                  <Star size={14} fill={p.premium ? 'currentColor' : 'none'} />
                </button>
                <button onClick={() => excluir(p)} title="Excluir" data-testid={`del-prof-${p.id}`} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
